if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.loadingSpinner = this.querySelector('.loading__spinner');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.hideErrors = this.dataset.hideErrors === 'true';

        if (!this.form || !this.submitButton) return;

        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      onSubmitHandler(evt) {
        const pastaelPdp = this.closest('pastael-pdp');
        if (pastaelPdp?.dataset.atcBound === 'true' && pastaelPdp.querySelector('pastael-pdp-bundle-builder')) {
          evt.preventDefault();
          return;
        }

        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.loadingSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const bundleBuilder = this.closest('pastael-pdp')?.querySelector('pastael-pdp-bundle-builder');
        if (bundleBuilder && typeof bundleBuilder.syncAllRows === 'function') {
          bundleBuilder.syncAllRows();
        }

        const formData = new FormData(this.form);
        if (bundleBuilder && typeof window.pastaelAppendFormAssociatedFields === 'function') {
          window.pastaelAppendFormAssociatedFields(this.form, formData);
        }
        const variantId = formData.get('id');

        if (!variantId) {
          this.handleErrorMessage(window.cartStrings?.error || 'Unable to add to cart.');
          this.submitButton.classList.remove('loading');
          this.loadingSpinner?.classList.add('hidden');
          this.submitButton.removeAttribute('aria-disabled');
          return;
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id).join(',')
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        const cartRoutes = window.routes || (typeof routes !== 'undefined' ? routes : null);
        const pubSubEvents = window.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);

        fetch(`${cartRoutes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (pubSubEvents && typeof publish === 'function') {
                publish(pubSubEvents.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
                });
              }
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const cartPerformance = typeof CartPerformance !== 'undefined' ? CartPerformance : null;
            const startMarker = cartPerformance?.createStartingMarker('add:wait-for-subscribers');
            if (!this.error && pubSubEvents && typeof publish === 'function') {
              publish(pubSubEvents.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                cartPerformance?.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (cartPerformance) {
                      cartPerformance.measure('add:paint-updated-sections', () => {
                        this.cart.renderContents(response);
                      });
                    } else {
                      this.cart.renderContents(response);
                    }
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (cartPerformance) {
              cartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.loadingSpinner?.classList.add('hidden');

            if (typeof CartPerformance !== 'undefined') {
              CartPerformance.measureFromEvent('add:user-action', evt);
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      getSelectedVariantFromDom() {
        const script = this.closest('product-info')?.querySelector('[data-selected-variant]');
        if (!script?.textContent) return null;
        try {
          return JSON.parse(script.textContent);
        } catch {
          return null;
        }
      }

      isPastaelPreOrderVariant(variant) {
        const pdp = this.closest('pastael-pdp');
        if (typeof window.pastaelIsPreOrderVariant === 'function') {
          return window.pastaelIsPreOrderVariant(variant, pdp);
        }
        return false;
      }

      getPastaelAtcLabelText() {
        const pdp = this.closest('pastael-pdp');
        const variant = this.getSelectedVariantFromDom();
        if (this.isPastaelPreOrderVariant(variant)) {
          return pdp?.dataset.atcLabelPreOrder || window.variantStrings?.preOrder || window.variantStrings?.addToCart;
        }
        return pdp?.dataset.atcLabelDefault || window.variantStrings?.addToCart;
      }

      getSubmitButtonLabelElement() {
        return this.submitButton?.querySelector('[data-pastael-atc-label]') || this.submitButtonText;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        const labelEl = this.getSubmitButtonLabelElement();

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && labelEl) labelEl.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (labelEl) labelEl.textContent = this.getPastaelAtcLabelText();
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${(window.routes || routes).cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
